import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { extractTenantFromSubdomain, getTenantConfig } from '../utils/tenantConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseApp, setFirebaseApp] = useState(null);
  const [auth, setAuth] = useState(null);
  const [tenantId, setTenantId] = useState('');

  useEffect(() => {
    try {
      const tenant = extractTenantFromSubdomain();
      console.log('Tenant ID:', tenant);
      setTenantId(tenant);
      
      const config = getTenantConfig(tenant);
      const app = initializeApp(config);
      const auth = getAuth(app);
      
      setFirebaseApp(app);
      setAuth(auth);

      // Set the tenant ID for authentication
      auth.tenantId = tenant;

      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setUser(user);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error initializing Firebase:', error);
      setLoading(false);
    }
  }, []);

  const signIn = async (email, password) => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      setUser(result.user);
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account',
        // Add additional OAuth 2.0 scopes if needed
        scope: [
          'profile',
          'email'
        ]
      });
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Google authentication is not enabled in Firebase Console. Please contact your administrator.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in popup was closed before completing the sign-in.');
      }
      throw error;
    }
  };

  const signInWithMicrosoft = async () => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      const provider = new OAuthProvider('microsoft.com');
      
      // Configure Microsoft Auth Provider for multi-tenant
      provider.setCustomParameters({
        // This enables multi-tenant authentication
        prompt: 'select_account',
        tenant: 'common', // 'common' enables multi-tenant authentication
        // Additional parameters for your tenant configuration
        // client_id: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
        // You can add additional scopes if needed
      });

      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
    } catch (error) {
      console.error('Error signing in with Microsoft:', error);
      // Provide more specific error messages
      if (error.code === 'auth/tenant-not-found') {
        throw new Error('Your organization is not configured for this application. Please contact your administrator.');
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error('Microsoft authentication is not enabled for this application.');
      }
      throw error;
    }
  };

  const signUp = async (email, password) => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      setUser(result.user);
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        tenantId,
        signIn,
        signInWithGoogle,
        signInWithMicrosoft,
        signUp,
        logout,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}; 